<template>
  <div v-if="auditPeriod">
    <v-row>
      <v-col>
        <v-card>
          <v-card-title>
            {{ auditPeriod.user.company.address.name }}
          </v-card-title>

          <v-simple-table class="table-no-hover">
            <template #default>
              <tbody>
                <tr>
                  <td>
                    {{ $t("user.name") }}
                  </td>

                  <td>
                    {{ auditPeriod.user.firstname }}
                    {{ auditPeriod.user.lastname }}
                  </td>
                </tr>

                <tr>
                  <td>
                    {{ $t("user.lastOnlineAt") }}
                  </td>

                  <td>
                    {{ new Date(auditPeriod.user.lastOnlineAt).toLocaleString() }}
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card>
      </v-col>

      <v-col cols="12" md="4" v-if="auditPeriod.mayGenerateComparison || auditPeriod.mayGenerateComparisonMatrix">
        <v-card>
          <v-list>
            <v-list-item v-if="auditPeriod.mayGenerateComparison" @click="downloadComparison">
              <v-list-item-icon>
                <v-icon>mdi-download</v-icon>
              </v-list-item-icon>

              <v-list-item-content>
                <v-list-item-title>
                  {{ $t("auditor.auditPeriod.downloadComparison") }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-list-item v-if="auditPeriod.mayGenerateComparisonMatrix" @click="downloadComparisonMatrix">
              <v-list-item-icon>
                <v-icon>mdi-download</v-icon>
              </v-list-item-icon>

              <v-list-item-content>
                <v-list-item-title>
                  {{ $t("auditor.auditPeriod.downloadComparisonMatrix") }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" v-if="mayGenerateInterview">
        <InterviewForm :user="user" :auditPeriod="auditPeriod" />
      </v-col>

      <template v-if="referencePrechecks.length > 0">
        <v-col cols="12">
          <h2 class="text-h5 mt-12">{{ $t("auditor.auditPeriod.referencePrechecks") }}</h2>
        </v-col>

        <v-col v-for="fund in referencePrechecks" :key="fund.id" cols="12" xl="6">
          <FundCard :fund="fund" :audit-period="auditPeriod" />
        </v-col>
      </template>

      <template v-if="nonReferencePrechecks.length > 0">
        <v-col cols="12">
          <h2 class="text-h5 mt-12">{{ $t("auditor.auditPeriod.prechecks") }}</h2>
        </v-col>

        <v-col v-for="fund in nonReferencePrechecks" :key="fund.id" cols="12" xl="6">
          <FundCard :fund="fund" :audit-period="auditPeriod" />
        </v-col>
      </template>

      <template v-if="newPrechecks.length > 0">
        <v-col cols="12">
          <h2 class="text-h5 mt-12">{{ $t("auditor.auditPeriod.newPrechecks") }}</h2>
        </v-col>

        <v-col v-for="fund in newPrechecks" :key="fund.id" cols="12" xl="6">
          <FundCard :fund="fund" :audit-period="auditPeriod" />
        </v-col>
      </template>

      <template v-if="referenceFunds.length > 0">
        <v-col cols="12">
          <h2 class="text-h5 mt-12">{{ $t("auditor.auditPeriod.referenceFunds") }}</h2>
        </v-col>

        <v-col v-for="fund in referenceFunds" :key="fund.id" cols="12" xl="6">
          <FundCard :fund="fund" :audit-period="auditPeriod" />
        </v-col>
      </template>

      <template v-if="nonReferenceFunds.length > 0">
        <v-col cols="12">
          <h2 class="text-h5 mt-12">{{ $t("auditor.auditPeriod.funds") }}</h2>
        </v-col>

        <v-col v-for="fund in nonReferenceFunds" :key="fund.id" cols="12" xl="6">
          <FundCard :fund="fund" :audit-period="auditPeriod" />
        </v-col>
      </template>

      <template v-if="newFunds.length > 0">
        <v-col cols="12">
          <h2 class="text-h5 mt-12">{{ $t("auditor.auditPeriod.newFunds") }}</h2>
        </v-col>

        <v-col v-for="fund in newFunds" :key="fund.id" cols="12" xl="6">
          <FundCard :fund="fund" :audit-period="auditPeriod" />
        </v-col>
      </template>
    </v-row>

    <v-row class="mt-12">
      <v-col cols="12">
        <AuditPeriodFiles :audit-period="auditPeriod" />
      </v-col>

      <v-col cols="12">
        <AuditPeriodFilesForm :audit-period="auditPeriod" type="auditor" />

        <template v-if="$auth.may(['audit:finish'])">
          <v-switch
            :input-value="auditPeriod.attachmentEnabled"
            :label="$t('auditor.auditPeriod.attachmentEnabled')"
            @change="updateAttachmentEnabled"
          ></v-switch>

          <v-snackbar v-model="attachmentEnabledSaved" color="secondary">
            <div class="text-center flex-grow-1">
              {{ $t("action.saved") }}
            </div>
          </v-snackbar>
        </template>
      </v-col>
    </v-row>

    <v-row class="mt-12">
      <v-col cols="12">
        <UserEmailForm :user="auditPeriod.user" />
      </v-col>

      <v-col v-if="$auth.may(['audit:finish'])" cols="12" md="4">
        <AuditPeriodAuditorForm :audit-period="auditPeriod" />
      </v-col>

      <v-col v-if="$auth.may(['audit:finish'])" cols="12" md="4">
        <ClosedAtForm :audit-period="auditPeriod" />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { filter } from "ramda";
import FundCard from "@/components/Audit/FundCard";
import AuditPeriodFiles from "@/components/AuditPeriod/AuditPeriodFiles";
import AuditPeriodFilesForm from "@/components/AuditPeriod/AuditPeriodFilesForm";
import UpdateAuditPeriodMutation from "@/gql/mutations/UpdateAuditPeriodMutation.gql";
import auditPeriodIsClosed from "@fng/common/auditPeriodIsClosed";
import download from "@/utils/download";
import AuditPeriodComparisonQuery from "@/gql/queries/AuditPeriodComparisonQuery.gql";
import AuditPeriodComparisonMatrixQuery from "@/gql/queries/AuditPeriodComparisonMatrixQuery.gql";
import mayGenerateInterview from "@fng/common/mayGenerateInterview";
import InterviewForm from "@/components/Audit/InterviewForm";
import AuditPeriodAuditorForm from "@/components/Audit/AuditPeriodAuditorForm";
import UserEmailForm from "@/components/Audit/User/UserEmailForm";
import ClosedAtForm from "@/components/Audit/ClosedAtForm.vue";

export default {
  components: {
    FundCard,
    AuditPeriodFiles,
    AuditPeriodFilesForm,
    InterviewForm,
    AuditPeriodAuditorForm,
    UserEmailForm,
    ClosedAtForm,
  },

  props: {
    user: {
      type: Object,
      required: true,
    },

    auditPeriod: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      attachmentEnabledSaved: false,
    };
  },

  computed: {
    referenceFunds() {
      return filter(
        (fund) => fund.state !== "NEW" && fund.referencingFunds.length > 0 && !fund.precheck,
        this.auditPeriod.funds
      );
    },

    nonReferenceFunds() {
      return filter(
        (fund) => fund.state !== "NEW" && fund.referencingFunds.length === 0 && !fund.precheck,
        this.auditPeriod.funds
      );
    },

    newFunds() {
      return filter((fund) => fund.state === "NEW" && !fund.precheck, this.auditPeriod.funds);
    },

    newPrechecks() {
      return filter((fund) => fund.state === "NEW" && fund.precheck, this.auditPeriod.funds);
    },

    referencePrechecks() {
      return filter(
        (fund) => fund.state !== "NEW" && fund.referencingFunds.length > 0 && fund.precheck,
        this.auditPeriod.funds
      );
    },

    nonReferencePrechecks() {
      return filter(
        (fund) => fund.state !== "NEW" && fund.referencingFunds.length === 0 && fund.precheck,
        this.auditPeriod.funds
      );
    },

    isCurrent() {
      return this.auditPeriod.year === new Date().getFullYear();
    },

    mayGenerateInterview() {
      return this.isCurrent && mayGenerateInterview(this.auditPeriod.funds);
    },
  },

  methods: {
    auditPeriodIsClosed,

    async downloadComparison() {
      const filename = `Sternevergleich_FNG-Siegel_${this.auditPeriod.year + 1}_letztes_Jahr_${
        this.user.company.address.name
      }.csv`;

      const result = await this.$apollo.query({
        query: AuditPeriodComparisonQuery,
        fetchPolicy: "network-only",
        variables: {
          id: this.auditPeriod.id,
        },
      });

      download(filename, result.data.auditPeriodComparison);
    },

    async downloadComparisonMatrix() {
      const filename = `Sternevergleich_FNG-Siegel_${this.auditPeriod.year + 1}_${this.user.company.address.name}.csv`;

      const result = await this.$apollo.query({
        query: AuditPeriodComparisonMatrixQuery,
        fetchPolicy: "network-only",
        variables: {
          id: this.auditPeriod.id,
        },
      });

      download(filename, result.data.auditPeriodComparisonMatrix);
    },

    async updateAttachmentEnabled(val) {
      await this.$apollo.mutate({
        mutation: UpdateAuditPeriodMutation,
        variables: {
          id: this.auditPeriod.id,
          input: {
            attachmentEnabled: val,
          },
        },
      });

      this.attachmentEnabledSaved = true;
    },

    formatDate(val) {
      return new Date(val).toLocaleString(undefined, {
        timeZone: "CET",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    },
  },
};
</script>
